import React from "react";
import PlaceholderImage from "@/components/PlaceholderImage";
import SectionReveal from "@/components/SectionReveal";
import { PAGE_BANNERS } from "@/data/assets";

const TIMELINE = [
  {
    year: "2010",
    title: "The Beginning",
    body: "MusclePharm launches in the USA with a focus on athlete-driven sports nutrition — clean formulas, premium ingredients, and no unnecessary noise.",
  },
  {
    year: "2013",
    title: "UFC Partnership",
    body: "MusclePharm becomes closely associated with the world's premier mixed martial arts organisation, a partnership that anchors the brand's combat sports DNA.",
  },
  {
    year: "Present",
    title: "Global Legacy",
    body: "A trusted international sports nutrition brand recognised by athletes across strength, endurance and combat categories.",
  },
  {
    year: "2025",
    title: "Arrival In India",
    body: "MusclePharm officially arrives in India through an exclusive partnership with MusclePro Nutrition Private Limited — bringing the Combat range to Indian athletes for the first time.",
  },
];

export default function About() {
  return (
    <>
      {/* ABOUT BANNER */}
      <section data-testid="about-hero" className="bg-white">
        <div className="relative">
          <PlaceholderImage
            label="About Page Banner"
            ratio="aspect-[21/9]"
            tone="dark"
            testId="about-banner"
            src={PAGE_BANNERS.about}
            alt="About MusclePharm"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
          <div className="absolute inset-0 mp-container flex items-center">
            <div className="text-left w-full max-w-4xl">
              <div
                className="font-heading uppercase tracking-[0.32em] text-[11px] mb-4 font-semibold"
                style={{ color: "var(--mp-green)" }}
              >
                About MusclePharm
              </div>
              <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl uppercase leading-[0.9] text-white">
                Forged in combat.<br />
                Built for athletes.
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* LEAD PARAGRAPH */}
      <section className="bg-white py-16 lg:py-20 border-b border-black/10">
        <div className="mp-container max-w-4xl">
          <SectionReveal>
            <div className="mp-eyebrow-green">Introduction</div>
            <p className="mt-4 font-body text-black/80 text-lg lg:text-xl leading-relaxed">
              MusclePharm is a premium international sports nutrition brand built on a single idea — create honest, focused formulas for athletes who train seriously. Over the last decade, the brand has earned its place alongside some of the world&apos;s toughest athletes, most notably through its long-standing association with UFC.
            </p>
            <p className="mt-4 font-body text-black/80 text-lg lg:text-xl leading-relaxed">
              Now, that same commitment arrives in India — through an exclusive partnership with MusclePro Nutrition Private Limited, launching with a focused two-product Combat range and backed by TrueMPN authenticity verification on every unit.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* EDITORIAL SPLIT */}
      <section className="bg-[color:var(--mp-off-white)] py-20 lg:py-28">
        <div className="mp-container grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <SectionReveal className="lg:col-span-6">
            <PlaceholderImage
              label="Brand Story Image"
              ratio="portrait"
              testId="about-image-1"
              src={PAGE_BANNERS.brandStory}
              alt="MusclePharm — UFC Legacy"
            />
          </SectionReveal>
          <SectionReveal delay={120} className="lg:col-span-6">
            <div className="mp-eyebrow-green">Global Legacy</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
              A brand built alongside the world&apos;s toughest athletes.
            </h2>
            <p className="mt-6 font-body text-black/75 leading-relaxed text-lg">
              From strength athletes to combat athletes, MusclePharm&apos;s reputation has been earned in gyms, weight rooms and arenas — not in marketing rooms. The brand&apos;s long-running relationship with UFC anchored its identity: uncompromising, disciplined, performance-first.
            </p>
            <p className="mt-4 font-body text-black/75 leading-relaxed">
              That identity carries into every product. Straightforward formulas. Premium ingredients. Nothing wasted. It is a philosophy that has shaped over a decade of sports nutrition — and one that now defines the Combat range in India.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* TIMELINE */}
      <section
        data-testid="about-timeline"
        className="bg-white py-20 lg:py-24 border-y border-black/10"
      >
        <div className="mp-container">
          <SectionReveal>
            <div className="mp-eyebrow-green">Milestones</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
              The MusclePharm timeline
            </h2>
          </SectionReveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TIMELINE.map((t, i) => (
              <SectionReveal key={t.year} delay={i * 100}>
                <div
                  data-testid={`timeline-item-${i}`}
                  className="h-full border-t-2 border-black pt-6"
                >
                  <div
                    className="font-display text-4xl leading-none"
                    style={{ color: "var(--mp-green-hover)" }}
                  >
                    {t.year}
                  </div>
                  <div className="mt-4 font-heading uppercase tracking-[0.16em] text-sm text-black font-semibold">
                    {t.title}
                  </div>
                  <p className="mt-3 font-body text-black/70 leading-relaxed text-sm">{t.body}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ARRIVAL IN INDIA */}
      <section
        data-testid="about-india"
        className="bg-[color:var(--mp-off-white)] py-20 lg:py-28"
      >
        <div className="mp-container grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <SectionReveal className="lg:col-span-6 order-2 lg:order-1">
            <div className="mp-eyebrow-green">Arrival In India</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
              Now officially in India.
            </h2>
            <p className="mt-6 font-body text-black/75 leading-relaxed text-lg">
              MusclePharm arrives in India through an exclusive partnership with <strong>MusclePro Nutrition Private Limited</strong>. The Indian portfolio launches with a focused Combat range — Combat 100% Whey and Combat Preworkout — with more products to follow.
            </p>
            <p className="mt-4 font-body text-black/75 leading-relaxed">
              Every unit sold in India is officially imported through authorised channels, backed by authenticity verification on the TrueMPN app, and distributed across the country by our authorised sellers <strong>Nutriride</strong> and <strong>ADS Gallery</strong>. No middlemen. No parallel imports. Just genuine product, delivered with the same standards MusclePharm is known for worldwide.
            </p>
          </SectionReveal>
          <SectionReveal delay={120} className="lg:col-span-6 order-1 lg:order-2">
            <PlaceholderImage
              label="About Page Banner · India"
              ratio="landscape"
              testId="about-image-2"
              src={PAGE_BANNERS.aboutIndia}
              alt="MusclePharm India — Combat Preworkout"
            />
          </SectionReveal>
        </div>
      </section>
    </>
  );
}
